<template>
  <FormComponent
    title="Add Route"
    backRoute="/app/admin/lane-management/lane/list"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #formActions>
      <v-btn color="primary" depressed :disabled="!isValid" @click="submitForm">
        Submit
      </v-btn>
      <v-btn class="ml-3" color="secondary" depressed @click="resetForm">
        Reset
      </v-btn>
    </template>

    <template #formContent>
      <v-form v-model="isValid" ref="laneForm">
        <v-row class="ma-0">
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <MultiSelect
              hide-details="auto"
              label="Source Location *"
              :items="sourceList"
              itemText="location_code"
              itemValue="id"
              :item-disabled="
                (item) => {
                  return item == laneDetails.mid_location;
                }
              "
              :returnObject="true"
              :rules="[(v) => !!v || 'Source Location is Required!']"
              v-model="laneDetails.source_location"
              :error-messages="formErrors.source_location"
              @change="
                delete formErrors.source_location;
                checkData();
              "
            />
          </v-col>
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <MultiSelect
              hide-details="auto"
              label="Mid Location *"
              :items="midList"
              itemText="location_code"
              itemValue="id"
              :returnObject="true"
              :item-disabled="
                (item) => {
                  return (
                    item == laneDetails.source_location ||
                    item == laneDetails.destination_location
                  );
                }
              "
              :rules="[(v) => !!v || 'Mid Location is Required!']"
              v-model="laneDetails.mid_location"
              :error-messages="formErrors.mid_location"
              @change="
                delete formErrors.mid_location;
                checkData();
              "
            />
          </v-col>
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <MultiSelect
              hide-details="auto"
              label="Destination Location"
              :items="destinationList"
              itemText="location_code"
              itemValue="id"
              :returnObject="true"
              :item-disabled="
                (item) => {
                  return item == laneDetails.mid_location;
                }
              "
              :clearable="true"
              v-model="laneDetails.destination_location"
              :error-messages="formErrors.destination_location"
              @change="
                delete formErrors.destination_location;
                checkData();
              "
            />
          </v-col>
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <MultiSelect
              hide-details="auto"
              label="Route Options"
              :items="directions"
              itemText="name"
              itemValue="route"
              :returnObject="true"
              v-model="selectedRoute"
              @change="loadRoute"
            />
          </v-col>
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <InputField
              hide-details="auto"
              label="Distance"
              :clearable="false"
              v-model="laneDetails.distance"
              :error-messages="formErrors.distance"
              @input="delete formErrors.distance"
            />
          </v-col>
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <InputField
              hide-details="auto"
              type="number"
              label="Total Time to reach (Minutes)"
              :clearable="true"
              v-model="laneDetails.duration"
              :error-messages="formErrors.duration"
              @input="delete formErrors.duration"
            />
          </v-col>
          <v-col cols="12" lg="3" class="Form-Columns-Dense">
            <InputField
              hide-details="auto"
              type="number"
              label="Bonus Amount"
              :clearable="true"
              v-model="laneDetails.bonus"
              :error-messages="formErrors.bonus"
              @input="delete formErrors.bonus"
            />
          </v-col>
          <v-col cols="12" class="Form-Columns-Dense">
            <GoogleMap
              mapWidth="100%"
              mapHeight="100%"
              ref="LaneMap"
              class="Map-Height"
              :hasMapInfo="true"
            >
              <template #mapInfo>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" class="d-flex align-center pa-2">
                      <img style="width: 18px" :src="sourceMarker" />
                      <span class="ml-4 text-body-2 font-weight-bold">
                        Source
                      </span>
                    </v-col>
                    <v-col cols="12" class="d-flex align-center pa-2">
                      <img style="width: 18px" :src="midMarker" />
                      <span class="ml-4 text-body-2 font-weight-bold">
                        Mid
                      </span>
                    </v-col>
                    <v-col cols="12" class="d-flex align-center pa-2">
                      <img style="width: 18px" :src="destinationMarker" />
                      <span class="ml-4 text-body-2 font-weight-bold">
                        Destination
                      </span>
                    </v-col>
                  </v-row>
                </v-card-text>
              </template>
            </GoogleMap>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </FormComponent>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import FormComponent from "@/components/General/FormComponent.vue";
import GoogleMap from "@/components/General/GoogleMap.vue";
import {
  sourceMarker,
  destinationMarker,
  midMarker,
} from "@/assets/images/mapIcons.js";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    FormComponent,
    InputField,
    MultiSelect,
    GoogleMap,
  },
  data() {
    return {
      sourceMarker,
      destinationMarker,
      midMarker,
      isValid: true,
      laneDetails: {},
      formErrors: {},

      sourceCoordinates: null,
      destinationCoordinates: null,
      midCoordinates: null,

      // Arrays for Select
      sourceList: [],
      midList: [],
      destinationList: [],

      directions: [],
      directionsTimeout: null,
      selectedRoute: {},
      markerTimeout: null,

      autocomplete: null,
      autoCompleteLocation: null,
      waypoints: [],
      waypointNames: [],
    };
  },
  methods: {
    resetForm() {
      this.laneDetails = {};
      this.$refs.laneForm.resetValidation();
    },
    checkData() {
      if (this.laneDetails.source_location) {
        if (typeof this.laneDetails.source_location == typeof {}) {
          this.sourceCoordinates = this.laneDetails.source_location.coordinates;
        } else {
          this.sourceCoordinates = this.laneDetails.source_coordinates;
        }
      }
      if (
        this.laneDetails.mid_location &&
        typeof this.laneDetails.mid_location == typeof {}
      ) {
        if (!this.laneDetails.destination_location) {
          this.destinationCoordinates =
            this.laneDetails.mid_location.coordinates;

          this.waypoints = [];
          this.midCoordinates = null;
        } else {
          this.waypoints = [];
          this.waypoints.push({
            location: {
              lat: this.laneDetails.mid_location.coordinates.latitude,
              lng: this.laneDetails.mid_location.coordinates.longitude,
            },
            stopover: false,
          });
          this.midCoordinates = this.laneDetails.mid_location.coordinates;
        }
      } else {
        if (
          this.laneDetails.mid_location &&
          typeof this.laneDetails.mid_location == "number"
        ) {
          if (!this.laneDetails.destination_location) {
            this.destinationCoordinates = this.laneDetails.mid_coordinates;
            this.waypoints = [];
            this.midCoordinates = null;
          } else {
            this.waypoints = [];
            this.waypoints.push({
              location: {
                lat: this.laneDetails.mid_coordinates.latitude,
                lng: this.laneDetails.mid_coordinates.longitude,
              },
              stopover: false,
            });
            this.midCoordinates = this.laneDetails.mid_coordinates;
          }
        }
      }
      if (this.laneDetails.destination_location) {
        if (typeof this.laneDetails.destination_location == typeof {}) {
          this.destinationCoordinates =
            this.laneDetails.destination_location.coordinates;
        } else if (this.laneDetails.destination_coordinates) {
          this.destinationCoordinates =
            this.laneDetails.destination_coordinates;
        }
      }

      this.waypointNames = [];
      this.getRoute();
    },
    submitForm() {
      this.$bus.$emit("showLoader", true);
      let data = {
        ...this.laneDetails,
      };

      if (
        this.laneDetails.source_location &&
        typeof this.laneDetails.source_location == typeof {}
      ) {
        data.source_location = this.laneDetails.source_location.id;
      }
      if (
        this.laneDetails.mid_location &&
        typeof this.laneDetails.mid_location == typeof {}
      ) {
        data.mid_location = this.laneDetails.mid_location.id;
      }
      if (
        this.laneDetails.destination_location &&
        typeof this.laneDetails.destination_location == typeof {}
      ) {
        data.destination_location = this.laneDetails.destination_location.id;
      } else {
        data.destination_location = null;
      }

      if (data.route) {
        data.route = data.route.features[0].geometry;
      }

      if (data.distance) {
        data.distance = parseFloat(data.distance.replace(/\,/g, ""));
      }

      this.$api.lane
        .addLane(data)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Route Added Successfully!",
            color: "success",
          });
          this.$router.push("/app/admin/lane-management/lane/list");
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't Add Route!",
            color: "error",
          });
          this.formErrors = err.data;
        });
    },

    getLocationList() {
      this.$bus.$emit("showLoader", true);

      this.$api.location
        .getLocationSelectList({ limit: "all" })
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.sourceList = res.data;
          this.midList = res.data;
          this.destinationList = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    async getRoute() {
      if (this.sourceCoordinates && this.destinationCoordinates) {
        if (this.directionsTimeout) {
          clearTimeout(this.directionsTimeout);
        }

        this.directionsTimeout = setTimeout(() => {
          this.$bus.$emit("showLoader", true);

          const directionConfig = {
            origin: {
              lat: this.sourceCoordinates.latitude,
              lng: this.sourceCoordinates.longitude,
            },
            destination: {
              lat: this.destinationCoordinates.latitude,
              lng: this.destinationCoordinates.longitude,
            },
            travelMode: "DRIVING",
            provideRouteAlternatives: true,
          };

          if (this.waypoints && this.waypoints.length) {
            directionConfig.waypoints = this.waypoints;
          }

          this.$refs.LaneMap.getDirections(directionConfig)
            .then(async (resp) => {
              const directions = resp.routes;
              this.$bus.$emit("showLoader", false);
              this.directions = [];

              let index = 0;
              while (directions.length > this.directions.length) {
                let durationInMins = 0;
                let durationInSeconds =
                  directions[index].legs[0].duration.value;
                if (durationInSeconds) {
                  durationInMins = Math.round(durationInSeconds / 60);
                }
                this.directions.push({
                  name: `Route ${index + 1}`,
                  duration: durationInMins,
                  distance: directions[index].legs[0].distance.text,
                  route: this.$refs.LaneMap.generateGeoJsonFromLineString(
                    await this.$refs.LaneMap.generateLineStringFromRoute(
                      directions[index]
                    )
                  ),
                });
                index++;
              }

              this.selectedRoute = this.directions[0];

              this.loadMarkers();
              this.loadRoute();
            })
            .catch((err) => {
              console.error(err);
              this.$bus.$emit("showLoader", false);
            });
        }, 200);
      }
    },

    getAutoComplete() {
      const interval = setInterval(() => {
        if (this.$refs.autocomplete && window.google && window.google.maps) {
          clearInterval(interval);

          let input = document.getElementById("autocomplete");

          this.autocomplete = new window.google.maps.places.Autocomplete(input);
          input.setAttribute("placeholder", "");

          this.autocomplete.addListener("place_changed", () => {
            try {
              let place = this.autocomplete.getPlace();
              const coordinates = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              };

              this.waypoints.push({
                location: coordinates,
                stopover: false,
              });

              this.waypointNames.push({
                name: place.formatted_address,
              });

              this.autoCompleteLocation = null;

              this.getRoute();
            } catch (error) {
              console.error(error);
            }
          });
        }
      }, 100);
    },

    removeWaypoint(index) {
      this.waypoints.splice(index, 1);
      this.waypointNames.splice(index, 1);
      this.getRoute();
    },

    loadRoute() {
      if (this.markerTimeout) {
        clearTimeout(this.markerTimeout);
      }
      this.markerTimeout = setTimeout(() => {
        this.laneDetails = {
          ...this.laneDetails,
          route: this.selectedRoute.route,
          distance: this.selectedRoute.distance,
          duration: this.selectedRoute.duration,
        };

        this.$refs.LaneMap.addGeoCollection(this.laneDetails.route);
      }, 500);
    },

    loadMarkers() {
      const interval = setInterval(() => {
        if (google && google.maps) {
          clearInterval(interval);
          let markers = [
            { ...this.sourceCoordinates, type: "source" },
            { ...this.destinationCoordinates, type: "destination" },
          ];
          if (this.midCoordinates && typeof this.midCoordinates == typeof {}) {
            markers.push({ ...this.midCoordinates, type: "mid" });
          }

          this.$refs.LaneMap.addMultipleMarkers(markers);
        }
      }, 400);
    },
  },

  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("lane");
      if (!permissions || !permissions.add) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.getLocationList();
      }
    }
  },

  mounted() {
    this.getAutoComplete();
  },
};
</script>

<style scoped>
.Map-Height {
  height: calc(100vh - 253px);
}
</style>
